import React from 'react';
import { CheckCircleIcon, KeyIcon, EnvelopeIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const VenueActivation = () => {
    return (
        <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl w-full space-y-8">
                <div className="text-center">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 mb-6">
                        <CheckCircleIcon className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2">Venue Account Created!</h2>
                    <p className="text-gray-400">Your venue has been successfully set up on World's High Street</p>
                </div>

                <div className="bg-gray-900 border border-gray-700 rounded-lg p-8">
                    <h3 className="text-xl font-semibold text-white mb-6">Activation Instructions</h3>
                    
                    <div className="space-y-6">
                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
                                    1
                                </div>
                            </div>
                            <div>
                                <h4 className="font-medium text-white mb-2">Check Your Email</h4>
                                <p className="text-gray-400">
                                    We've sent activation instructions to your contact email address. 
                                    Please check your inbox and spam folder for an email from World's High Street.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
                                    2
                                </div>
                            </div>
                            <div>
                                <h4 className="font-medium text-white mb-2">Verify Your Account</h4>
                                <p className="text-gray-400">
                                    Click the verification link in the email to activate your venue account. 
                                    This will confirm your email address and enable full access to your dashboard.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
                                    3
                                </div>
                            </div>
                            <div>
                                <h4 className="font-medium text-white mb-2">First Time Login</h4>
                                <p className="text-gray-400">
                                    Once verified, use your email and password to sign in to your venue dashboard. 
                                    You'll be guided through the initial setup process.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
                                    4
                                </div>
                            </div>
                            <div>
                                <h4 className="font-medium text-white mb-2">Complete Venue Setup</h4>
                                <p className="text-gray-400">
                                    Add your classes, configure your venue settings, set up your schedule, 
                                    and start accepting bookings from your users.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 text-center">
                        <KeyIcon className="h-8 w-8 text-emerald-400 mx-auto mb-4" />
                        <h4 className="font-medium text-white mb-2">Share Your Venue Code</h4>
                        <p className="text-gray-400 text-sm">
                            Your users will need your venue's short code to sign up and access your classes. 
                            You can find this in your dashboard once activated.
                        </p>
                    </div>

                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 text-center">
                        <EnvelopeIcon className="h-8 w-8 text-blue-400 mx-auto mb-4" />
                        <h4 className="font-medium text-white mb-2">Need Help?</h4>
                        <p className="text-gray-400 text-sm">
                            Our support team is here to help you get started. Contact us if you have any questions 
                            about setting up your venue.
                        </p>
                    </div>
                </div>

                <div className="bg-emerald-900/20 border border-emerald-700 rounded-lg p-6">
                    <div className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-emerald-400 mt-0.5" />
                        <div>
                            <h4 className="text-emerald-400 font-medium mb-2">Payment Setup Complete</h4>
                            <p className="text-emerald-300 text-sm">
                                Your payment method has been successfully configured with GoCardless. 
                                Your billing will begin once your account is fully activated and you start using the platform.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="text-center space-y-4">
                    <button
                        onClick={() => window.location.href = '/login'}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
                    >
                        Go to Login Page
                        <ArrowRightIcon className="ml-2 h-5 w-5" />
                    </button>
                    
                    <p className="text-gray-400 text-sm">
                        Ready to sign in? Use the email and password you created during registration.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default VenueActivation;
